import { RunnerDetailsSection, type SectionStatus } from "@/components/runners/details/RunnerDetailsSection";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC, useMemo } from "react";
import { CloudFormationStack } from "@/components/runners/details/CloudFormationStack";
import { LinuxRunnerSetup } from "@/components/runners/details/LinuxRunnerSetup";
import { GCPRunnerSetup } from "@/components/runners/details/GCPRunnerSetup";
import { RunnerProvider, RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";

export const RunnerProviderSetup: FC<{ runner: PlainMessage<Runner> | undefined; hasSignedUp: boolean }> = ({
    runner,
    hasSignedUp,
}) => {
    const title = useMemo(() => {
        switch (runner?.provider) {
            case RunnerProvider.AWS_EC2:
                if (!hasSignedUp) {
                    return "Set up your AWS CloudFormation"
                }
                return "Runner Details";
            case RunnerProvider.MANAGED:
                return "Ona Cloud Runner";
            default:
                return "Runner Details";
        }
    }, [hasSignedUp, runner]);
    const setupComponent = useMemo(() => {
        if (!runner) {
            // Return empty div for skeleton loading state
            return <div className="h-[200px]" />;
        }

        switch (runner.provider) {
            case RunnerProvider.AWS_EC2:
                return <CloudFormationStack runner={runner} hasStack={hasSignedUp} />;
            case RunnerProvider.LINUX_HOST:
                return <LinuxRunnerSetup runner={runner} hasSignedUp={hasSignedUp} />;
            case RunnerProvider.GCP:
                return <GCPRunnerSetup runner={runner} hasSignedUp={hasSignedUp} />;
            case RunnerProvider.MANAGED:
                return (
                    <div className="flex items-center gap-3">
                        <div className="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                            <svg className="size-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div>
                            <Text className="text-base font-semibold">Fully managed by Ona Cloud</Text>
                            <Text className="text-sm text-content-secondary">
                                This runner is automatically provisioned, configured, and maintained. No setup required.
                            </Text>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="p-4">
                        <p className="text-content-secondary">
                            No setup instructions available for this provider type.
                        </p>
                    </div>
                );
        }
    }, [runner, hasSignedUp]); // Only recreate when runner or hasSignedUp changes

    if (runner && runner.kind !== RunnerKind.REMOTE) {
        return null;
    }

    let status: SectionStatus = "loading";
    if (runner) {
        status = hasSignedUp ? "complete" : "required";
    }

    return (
        <RunnerDetailsSection status={status} title={title} hideStatus>
            {setupComponent}
        </RunnerDetailsSection>
    );
};
